import { useGetLoginLogs } from "@/api/queries/audit-logs";
import type { LoginLogItem } from "@/api/queries/audit-logs/types";
import { LoginSelect } from "@/components/login-select";
import { TablePagination } from "@/components/table/table-pagination";
import { type SortDirection, TableSort } from "@/components/table/table-sort";
import { DatePickerWithRange } from "@/components/ui/date-picker";
import { PayloadViewer } from "@/components/ui/payload-viewer";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  type TableColumn,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { UserSelect } from "@/components/user-select";
import { LOGIN_ACTIONS } from "@/constants/actions";
import { formatDate } from "@/lib/date";
import { Fragment, useMemo } from "react";

import { useLoginLogsState } from "../hooks/use-login-logs-state";

export const LoginLogsTable = () => {
  const { state, setState } = useLoginLogsState();
  const { data, isLoading } = useGetLoginLogs(state);

  const actionOptions = useMemo(() => {
    return Object.entries(LOGIN_ACTIONS).map(([value, label]) => ({ value, label }));
  }, []);

  const columns = useMemo<TableColumn<LoginLogItem>[]>(() => {
    return [
      {
        key: "perform_by",
        className: "min-w-[120px] md:min-w-unset",
        label: (
          <UserSelect
            placeholder="Perform By"
            value={state.performByUserId}
            onValueChange={(value) => setState({ performByUserId: value })}
          />
        ),
        render: (log) => (
          <TableCell className="font-medium">{log.performer?.username ?? "System"}</TableCell>
        ),
      },
      {
        key: "target",
        className: "min-w-[120px] md:min-w-unset",
        label: (
          <LoginSelect
            placeholder="Target Login"
            value={state.targetLoginId}
            onValueChange={(value) => setState({ targetLoginId: value })}
          />
        ),
        render: (log) => <TableCell className="font-medium">{log.targetLogin?.username}</TableCell>,
      },
      {
        key: "action",
        label: (
          <Select
            value={state.actions?.[0] ?? null}
            onValueChange={(action) => setState({ actions: action ? [action] : [] })}
          >
            <SelectTrigger>
              {state.actions?.length ? (
                <>{state.actions.map((action) => LOGIN_ACTIONS[action]).join(",")}</>
              ) : (
                "Action"
              )}
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={null}>Select Action</SelectItem>
              {actionOptions.map((action) => (
                <SelectItem value={action.value}>{action.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ),
        sortable: true,
        render: (log) => <TableCell>{LOGIN_ACTIONS[log.action]}</TableCell>,
      },
      {
        key: "payload",
        label: "Payload",
        sortable: true,
        render: (log) => (
          <TableCell>
            <PayloadViewer className="max-w-2xs" payload={log.payload} />
          </TableCell>
        ),
      },

      {
        key: "createdAt",
        label: "Created",
        sortable: true,
        render: (log) => <TableCell>{formatDate(new Date(log.createdAt))}</TableCell>,
      },
    ];
  }, [state]);

  const handleSortChange = (key: string) => {
    return (sortDirection: "asc" | "desc" | undefined) => {
      setState({ sortBy: key, orderBy: sortDirection });
    };
  };

  return (
    <>
      <div className="flex flex-wrap gap-4 mb-8 justify-between md:justify-start">
        <div className="min-w-[180px]">
          <DatePickerWithRange
            date={{
              from: state.minCreatedAt ? new Date(state.minCreatedAt) : undefined,
              to: state.maxCreatedAt ? new Date(state.maxCreatedAt) : undefined,
            }}
            onSelect={(range) =>
              setState({
                minCreatedAt: range?.from?.toISOString(),
                maxCreatedAt: range?.to?.toISOString(),
              })
            }
            label="Created"
          />
        </div>
      </div>
      <div className="min-w-0 overflow-hidden flex">
        <div className="rounded-xl border overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((col) => (
                  <TableHead key={col.key} className={col.className}>
                    <div className="flex items-center gap-1">
                      {col.label}{" "}
                      {col.sortable && (
                        <TableSort
                          onChange={handleSortChange(col.key)}
                          direction={
                            col.key === state.sortBy ? (state.orderBy as SortDirection) : undefined
                          }
                        />
                      )}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <div className="flex items-center justify-center p-8">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && (data?.data || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <div className="flex items-center justify-center p-8 text-muted-foreground">
                      No login logs found
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {(data?.data || []).map((log) => (
                <TableRow key={log.id}>
                  {columns.map((col) => (
                    <Fragment key={col.key}>{col.render(log)}</Fragment>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex justify-end">
            <TablePagination
              page={state.page ?? 1}
              pageSize={state.pageSize ?? 20}
              onPageSizeChange={(pageSize) => setState({ pageSize })}
              onPageChange={(page) => setState({ page })}
              total={data?.pagination.total ?? 1}
            />
          </div>
        </div>
      </div>
    </>
  );
};
